import { NodeDocContent } from "./nodeDocsData";

export const exportNodesDocs: Record<string, NodeDocContent> = {
  "FILE-DATABASE": {
    title: "MongoDB Connector",
    subtitle: "Database Integration",
    color: "emerald",
    badge: "Export",
    description:
      "Connects to a MongoDB instance to insert or update documents. This node allows you to store processed workflow data directly into your own data warehouse.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Ensure your MongoDB instance is accessible from the workflow engine. You may need to whitelist specific IP addresses depending on your network configuration.
        </div>

        <section>
          <h2>How it works</h2>
          <p>Provide your connection details, select a target collection, and map the data you want to store. The node inserts a new document into MongoDB each time the workflow runs.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Configure connection</p>
                <p class="wf-step-desc">Provide your MongoDB connection string, database name, and target collection.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Map data</p>
                <p class="wf-step-desc">Map variables from previous nodes (OCR results, LLM outputs, etc.) to the input field.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Insert document</p>
                <p class="wf-step-desc">The mapped data is saved as a new document in the specified MongoDB collection.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Connection String</code></td>
                <td>The MongoDB URI, e.g. <code>mongodb+srv://user:pass@cluster.example.com</code>. Include auth credentials in the string.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Database</code></td>
                <td>The name of the database where the collection is located.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Collection</code></td>
                <td>The specific collection where the document will be inserted.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Data</code></td>
                <td>The mapped input data to save. Typically connected from a Transform or extraction node.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Include authentication credentials directly in the connection string.</li>
            <li>Use a Transform node before this one to shape data into your exact document schema.</li>
            <li>Test the connection with a small payload before running full batch workflows.</li>
          </ul>
        </div>
      </div>
    `,
  },

  WEBHOOK: {
    title: "Webhook Notification",
    subtitle: "External API Integration",
    color: "amber",
    badge: "Export",
    description:
      "Sends an HTTP request to a target URL with the workflow's processed data. Use this to notify downstream systems, trigger external automations, or sync data in real-time.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Webhooks are ideal for <strong>event-driven architectures</strong>. They eliminate the need for downstream systems to poll the DMS API for updates.
        </div>

        <section>
          <h2>How it works</h2>
          <p>When the workflow reaches this node, it sends an HTTP request containing the processed data to your specified endpoint. The receiving system can then act on the data immediately.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Configure endpoint</p>
                <p class="wf-step-desc">Set the target URL and HTTP method. POST is standard for sending data payloads.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Select payload</p>
                <p class="wf-step-desc">Choose which output data (JSON) to include in the request body.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Send notification</p>
                <p class="wf-step-desc">The request is sent when the workflow reaches this node during execution.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>URL</code></td>
                <td>The endpoint that will receive the request. Must be HTTPS for security.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Method</code></td>
                <td>HTTP method — POST (default), GET, PUT, DELETE, or PATCH.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Auth Header</code></td>
                <td>Optional API key or Bearer token for authenticating with the destination.</td>
                <td>No</td>
              </tr>
              <tr>
                <td><code>Payload</code></td>
                <td>The data to send in the request body. Map from upstream node outputs.</td>
                <td>No</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <ul class="wf-list">
            <li><strong>CRM sync</strong> — push extracted customer data to Salesforce, HubSpot, or your internal CRM.</li>
            <li><strong>Slack notifications</strong> — send a message to a Slack channel when a document finishes processing.</li>
            <li><strong>ERP integration</strong> — forward invoice data to your accounting system in real-time.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Always use HTTPS endpoints for production webhook destinations.</li>
            <li>Include an API key or auth header if the destination requires authentication.</li>
            <li>Test with a service like webhook.site to inspect payloads during development.</li>
          </ul>
        </div>
      </div>
    `,
  },

  EXCEL: {
    title: "Export to Excel",
    subtitle: "Tabular Reporting",
    color: "green",
    badge: "Export",
    description:
      "Generates a structured .xlsx spreadsheet file from list data. Perfect for creating summary reports, audit logs, or data exports for manual analysis.",
    content: `
      <div class="space-y-10">
        <section>
          <h2>How it works</h2>
          <p>The node takes an array of objects and converts it into an Excel spreadsheet. Each object becomes a row, and you define how JSON fields map to column headers.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Provide data source</p>
                <p class="wf-step-desc">The node expects an <strong>array of objects</strong>. Each object represents one row in the spreadsheet.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Define column mapping</p>
                <p class="wf-step-desc">Map JSON fields (e.g. <code>invoice_id</code>) to Excel column headers (e.g. "Invoice Reference").</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Generate file</p>
                <p class="wf-step-desc">A downloadable .xlsx file is produced as the node's output.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Key features</h2>
          <ul class="wf-list">
            <li><strong>Dynamic filenames</strong> — use variables in the filename, like <code>{{date}}_report.xlsx</code>.</li>
            <li><strong>Dot notation</strong> — access nested object fields, e.g. <code>customer.details.email</code>.</li>
            <li><strong>Auto-formatting</strong> — column widths adjust to content, headers are bolded automatically.</li>
          </ul>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Data Source</code></td>
                <td>Array of objects from a previous node. Each object becomes one row.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Column Mapping</code></td>
                <td>Map JSON field names to human-readable Excel column headers.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Filename</code></td>
                <td>Name for the generated file. Supports <code>{{variable}}</code> syntax.</td>
                <td>No</td>
              </tr>
            </tbody>
          </table>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use a Transform node before this one to clean and reshape data for the report.</li>
            <li>Dot notation lets you access deeply nested fields without flattening first.</li>
            <li>Combine with OneDrive Upload or Webhook to automatically deliver the generated report.</li>
          </ul>
        </div>
      </div>
    `,
  },

  WORD: {
    title: "Word Document",
    subtitle: "Document Generation",
    color: "blue",
    badge: "Export",
    description:
      "Creates a polished .docx document using AI. Ideal for generating summary reports, legal notices, or automated correspondence based on extracted data.",
    content: `
      <div class="space-y-10">
        <section>
          <h2>How it works</h2>
          <p>The Word Document node uses AI to transform raw text or structured data into a professionally formatted .docx file. You provide a natural language prompt to guide the AI on formatting, structure, and tone.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Provide input content</p>
                <p class="wf-step-desc">Accepts raw text or JSON data from connected nodes (OCR output, summaries, etc.).</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Write AI prompt</p>
                <p class="wf-step-desc">Use a natural language prompt to instruct the AI on how to format, style, and structure the document.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Generate document</p>
                <p class="wf-step-desc">A downloadable .docx file is generated as the node's output.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Input Content</code></td>
                <td>The raw text or structured data to use as the document's source material.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>AI Prompt</code></td>
                <td>Natural language instructions for the AI on how to format and organize the content.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">Summary reports</p>
              <p class="wf-use-case-flow">OCR &rarr; AI Summary &rarr; Word Document &rarr; Email</p>
              <p>Generate executive summary reports from extracted document content and deliver via email.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Automated correspondence</p>
              <p class="wf-use-case-flow">Form Data &rarr; Transform &rarr; Word Document &rarr; OneDrive</p>
              <p>Create personalized letters, notices, or contracts from structured form data.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Be specific in your AI prompt — mention headings, bullet points, or table formatting you want.</li>
            <li>The AI handles paragraph structure, styling, and professional formatting automatically.</li>
            <li>Chain with OneDrive Upload or Email nodes for automatic delivery.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "ONEDRIVE-UPLOAD": {
    title: "OneDrive Upload",
    subtitle: "Cloud Storage",
    color: "sky",
    badge: "Export",
    description:
      "Uploads generated files directly to a specific folder in Microsoft OneDrive. This serves as the final archival step for many automated document workflows.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Requires Microsoft OAuth 2.0 authentication. Folder browsing is available once connected.
        </div>

        <section>
          <h2>Setup</h2>
          <p>Connect your Microsoft account and select a destination folder. The node will upload files to that folder each time the workflow runs.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Authenticate</p>
                <p class="wf-step-desc">Connect your Microsoft account via OAuth 2.0 flow.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Select folder</p>
                <p class="wf-step-desc">Browse and select the destination folder in your OneDrive.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Map file input</p>
                <p class="wf-step-desc">Connect a file output (from PDF Generator, Excel Export, etc.) to this node's input.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num wf-step-done">&#10003;</div>
              <div>
                <p class="wf-step-title">Upload on execution</p>
                <p class="wf-step-desc">The file is uploaded to the selected folder each time the workflow runs.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Credential</code></td>
                <td>The authenticated Microsoft account. Set up via OAuth flow.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Folder</code></td>
                <td>The OneDrive folder where files will be uploaded.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>File Input</code></td>
                <td>The file buffer to upload. Connect from PDF Generator, Excel Export, or similar nodes.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Complete OAuth authentication before configuring the folder.</li>
            <li>Works with any file-producing node — PDF Generator, Excel Export, Word Document, etc.</li>
            <li>Select a specific subfolder to keep uploaded files organized.</li>
          </ul>
        </div>
      </div>
    `,
  },
  "OUTLOOK-SEND": {
    title: "Outlook Send",
    subtitle: "Email Communication",
    color: "blue",
    badge: "Export",
    description:
      "Sends professional emails via Microsoft Outlook. Supports AI-driven template generation, dynamic variable mapping, and high-fidelity HTML formatting.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Requires Microsoft OAuth 2.0 authentication. Ensure your account has permissions to send emails.
        </div>

        <section>
          <h2>How it works</h2>
          <p>The node takes your message body and subject, optionally enhances them with AI, and dispatches an email via the Microsoft Graph API.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Connect Account</p>
                <p class="wf-step-desc">Link your Outlook account using the secure OAuth flow.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Configure Message</p>
                <p class="wf-step-desc">Set the recipient, subject, and body. Use {{variable}} syntax to map data from previous nodes.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">AI Formatting</p>
                <p class="wf-step-desc">Optionally provide instructions for AI to format your content into a professional HTML template.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    `,
  },
  "GMAIL-SEND": {
    title: "Gmail Send",
    subtitle: "Email Communication",
    color: "red",
    badge: "Export",
    description:
      "Sends professional emails via Google Gmail. Supports AI-driven template generation, dynamic variable mapping, and high-fidelity HTML formatting.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Requires Google OAuth 2.0 authentication. Ensure your account has permissions to send emails via Gmail.
        </div>

        <section>
          <h2>How it works</h2>
          <p>The node takes your message body and subject, optionally enhances them with AI, and dispatches an email via the Google Gmail API.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Connect Account</p>
                <p class="wf-step-desc">Link your Gmail account using the secure OAuth flow.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Configure Message</p>
                <p class="wf-step-desc">Set the recipient, subject, and body. Use {{variable}} syntax to map data from previous nodes.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">AI Formatting</p>
                <p class="wf-step-desc">Optionally provide instructions for AI to format your content into a professional HTML template.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    `,
  },
};
